import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AseguradoLanding from "@/pages/AseguradoLanding";
import Landing from "@/pages/Landing";
import Inbox from "@/pages/Inbox";
import Upload from "@/pages/Upload";
import Review from "@/pages/Review";
import Analytics from "@/pages/Analytics";
import Produccion from "@/pages/Produccion";
import Rutas from "@/pages/Rutas";
import Peritos from "@/pages/Peritos";
import PeritoDetail from "@/pages/PeritoDetail";
import Aseguradoras from "@/pages/Aseguradoras";
import AseguradoraDetail from "@/pages/AseguradoraDetail";
import Repositorio from "@/pages/Repositorio";
import Gabinetes from "@/pages/Gabinetes";
import GabineteDetail from "@/pages/GabineteDetail";
import CompanySettings from "@/pages/CompanySettings";
import BaremoEditor from "@/pages/BaremoEditor";
import VerifyInforme from "@/pages/VerifyInforme";
import ChatPage from "@/pages/Chat";
import LiquidacionesAseguradoras from "@/pages/LiquidacionesAseguradoras";
import RendimientoPeritos from "@/pages/RendimientoPeritos";
import ClientesFacturacion from "@/pages/ClientesFacturacion";
import Facturas from "@/pages/Facturas";
import MiPerfil from "@/pages/MiPerfil";
import MiControlGestiones from "@/pages/MiControlGestiones";
import AdminControlGestiones from "@/pages/AdminControlGestiones";
import Meteo from "@/pages/Meteo";
import NewsClimatic from "@/pages/NewsClimatic";
import EmailIntake from "@/pages/EmailIntake";
import Partners from "@/pages/Partners";
import Calendar from "@/pages/Calendar";
import CalendarSettings from "@/pages/CalendarSettings";
import Reparadores from "@/pages/Reparadores";
import SolicitudesDocPage from "@/pages/SolicitudesDocPage";
import GestoresPage from "@/pages/GestoresPage";
import MobileAgenda from "@/mobile/MobileAgenda";
import MobileEventDetail from "@/mobile/MobileEventDetail";
import MobileExpediente from "@/mobile/MobileExpediente";
import MobileExpedienteFotos from "@/mobile/MobileExpedienteFotos";
import MobileExpedienteDocs from "@/mobile/MobileExpedienteDocs";
import MobileExpedientes from "@/mobile/MobileExpedientes";
import MobileMiControl from "@/mobile/MobileMiControl";
import MobileMeteoLite from "@/mobile/MobileMeteoLite";

// Wrapper que monta ChatProvider sólo en rutas protegidas (no en /, /login, /verify).
// Así evitamos polling y errores en la landing pública.
function AuthedShell() {
  return (
    <ChatProvider>
      <Layout />
    </ChatProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" closeButton />
        <Routes>
          {/* Landing pública oculta de momento: la raíz redirige al login.
              Cuando se vuelva a habilitar, restaurar `<Route path="/" element={<Landing />} />`. */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/share/:token" element={<AseguradoLanding />} />
          <Route path="/verify/:attId" element={<VerifyInforme />} />

          <Route element={<ProtectedRoute><AuthedShell /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Navigate to="/analytics" replace />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/produccion" element={<Produccion />} />
            <Route path="/rutas" element={<Rutas />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/calendar/configuracion" element={<CalendarSettings />} />
            <Route path="/mi-perfil" element={<MiPerfil />} />
            <Route path="/mi-control-gestiones" element={<MiControlGestiones />} />
            <Route path="/admin/control-gestiones" element={<AdminControlGestiones />} />
            <Route path="/meteo" element={<Meteo />} />
            <Route path="/news-climatic" element={<NewsClimatic />} />
            <Route path="/admin/email-intake" element={<EmailIntake />} />
            <Route path="/repositorio" element={<Repositorio />} />
            <Route path="/repositorio/:aseguradoraId" element={<Repositorio />} />
            <Route path="/review/:id" element={<Review />} />
            <Route path="/solicitudes-doc" element={<Navigate to="/gestores?tab=documentacion" replace />} />
            <Route path="/gestores" element={<GestoresPage />} />
            <Route path="/admin/peritos" element={<Peritos />} />
            <Route path="/admin/peritos/:id" element={<PeritoDetail />} />
            <Route path="/admin/aseguradoras" element={<ProtectedRoute adminOnly><Aseguradoras /></ProtectedRoute>} />
            <Route path="/admin/aseguradoras/:id" element={<ProtectedRoute adminOnly><AseguradoraDetail /></ProtectedRoute>} />
            <Route path="/admin/aseguradoras/:aseguradoraId/baremos/:baremoId" element={<ProtectedRoute adminOnly><BaremoEditor /></ProtectedRoute>} />
            <Route path="/admin/reparadores" element={<Reparadores />} />
            <Route path="/admin/gabinetes" element={<Gabinetes />} />
            <Route path="/admin/gabinetes/:id" element={<GabineteDetail />} />
            <Route path="/admin/configuracion" element={<ProtectedRoute adminOnly allowSenior><CompanySettings /></ProtectedRoute>} />
            <Route path="/admin/liquidaciones-aseguradoras" element={<ProtectedRoute adminOnly><LiquidacionesAseguradoras /></ProtectedRoute>} />
            <Route path="/admin/clientes-facturacion" element={<ProtectedRoute adminOnly><ClientesFacturacion /></ProtectedRoute>} />
            <Route path="/admin/facturas" element={<ProtectedRoute adminOnly><Facturas /></ProtectedRoute>} />
            <Route path="/admin/partners" element={<ProtectedRoute adminOnly><Partners /></ProtectedRoute>} />
            <Route path="/admin/rendimiento" element={<ProtectedRoute adminOnly><RendimientoPeritos /></ProtectedRoute>} />
          </Route>

          {/* === Modo móvil (perito en campo) — rutas aisladas con su propio shell === */}
          <Route element={<ProtectedRoute><ChatProvider><Outlet /></ChatProvider></ProtectedRoute>}>
            <Route path="/m" element={<Navigate to="/m/agenda" replace />} />
            <Route path="/m/agenda" element={<MobileAgenda />} />
            <Route path="/m/agenda/event/:eventId" element={<MobileEventDetail />} />
            <Route path="/m/expedientes" element={<MobileExpedientes />} />
            <Route path="/m/mi-control" element={<MobileMiControl />} />
            <Route path="/m/meteo" element={<MobileMeteoLite />} />
            <Route path="/m/expediente/:id" element={<MobileExpediente />} />
            <Route path="/m/expediente/:id/fotos" element={<MobileExpedienteFotos />} />
            <Route path="/m/expediente/:id/docs" element={<MobileExpedienteDocs />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
